import type { InputHTMLAttributes } from "react";
import { tokens, stateTransition, hairline } from "../tokens";
import { Label } from "./Label";

interface TextFieldProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "className" | "id"> {
	id: string;
	// Micro cap label sitting above the field.
	label: string;
	// Field level validation message; renders in the rank 1 accent and marks the input invalid.
	error?: string | null;
}

// A labelled input. The edge is a hairline that thickens to the emphasis stroke on focus, or to the
// rank 1 accent when the field is invalid. No filled box.
export function TextField({ id, label, error, style, ...rest }: TextFieldProps) {
	const invalid = Boolean(error);
	return (
		<div style={{ display: "flex", flexDirection: "column", gap: tokens.space.sm }}>
			<Label as="label" htmlFor={id}>
				{label}
			</Label>
			<input
				id={id}
				aria-invalid={invalid}
				aria-describedby={invalid ? `${id}-error` : undefined}
				style={{
					minHeight: tokens.touch.min,
					padding: `0 ${tokens.space.md}px`,
					background: tokens.color.surface.raised,
					border: hairline(invalid ? tokens.color.accent.red : tokens.color.stroke.hairline),
					borderRadius: tokens.radius.sm,
					color: tokens.color.text.primary,
					fontSize: tokens.size.bodyLg,
					transition: stateTransition("border-color"),
					...style,
				}}
				{...rest}
			/>
			{invalid && (
				<span id={`${id}-error`} style={{ ...tokens.type.microcap, color: tokens.color.accent.red }}>
					{error}
				</span>
			)}
		</div>
	);
}
